import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner } from 'react-bootstrap';
import { Check, Calendar, Users, Home } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../context/authcontext';
import api from '../../utils/axiosconfig';
import './Confirmation.css';

const ConfirmationPage = () => {
  const [bookingDetails, setBookingDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { accessToken } = useAuth();
  const { id } = useParams();

  useEffect(() => {
    const fetchBookingDetails = async () => {
      setIsLoading(true);
      try {
        if (!id) {
          throw new Error('Booking ID not found');
        }
        const response = await api.get(`/booking/${id}`, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        setBookingDetails(response.data.data);
      } catch (err) {
        console.error('Error fetching booking details:', err);
        setError('Failed to fetch booking details. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookingDetails();
  }, [accessToken, id]);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <h2>Error</h2>
          <p>{error}</p>
          <Link to="/">
            <Button variant="primary">Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="confirmation-page">
      <section className="section-sub-banner bg-9">
        <div className="awe-overlay"></div>
        <div className="sub-banner">
          <div className="container">
            <div className="text text-center">
              <h2>Confirmation</h2>
            </div>
          </div>
        </div>
      </section>

      <Container className="mt-n5 margin-20">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-5">
                <div className="text-center mb-4">
                  <div className="bg-success bg-opacity-10 d-inline-block rounded-circle p-3 mb-3">
                    <Check className="text-success" size={40} />
                  </div>
                  <h2 className="h3 mb-4">Your booking is confirmed</h2>
                </div>

                <Card className="bg-light mb-5">
                  <Card.Body>
                    <Row className="align-items-center mb-2">
                      <Col>
                        <span className="text-muted">Booking ID</span>
                      </Col>
                      <Col className="text-end">
                        <strong>
                        {'SRIHS' + bookingDetails.id.toString().padStart(4, '0')}</strong>
                      </Col>
                    </Row>
                    <Row className="align-items-center">
                      <Col>
                        <span className="text-muted">Total Amount</span>
                      </Col>
                      <Col className="text-end">
                        <strong className="h4">₹{parseFloat(bookingDetails.total_amount).toFixed(2)}</strong>
                      </Col>
                    </Row>
                    <Row className="align-items-center mt-2">
                      <Col>
                        <span className="text-muted">Payment Status</span>
                      </Col>
                      <Col className="text-end">
                        <strong>{bookingDetails.is_paid ? 'Paid' : 'Pending'}</strong>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {bookingDetails.special_comments && (
                  <Card className="mb-5">
                    <Card.Body>
                      <Card.Title>Special Comments</Card.Title>
                      <Card.Text>{bookingDetails.special_comments}</Card.Text>
                    </Card.Body>
                  </Card>
                )}

                <div className="d-flex flex-column flex-sm-row justify-content-center gap-3">
                  <Link to="/" >
                    <button className="awe-btn">Back to Home</button>
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ConfirmationPage;